/**
 * list-specs — enumerate OpenAPI/AsyncAPI specs in the project's specs/ directory.
 *
 * Returns one entry per spec with integration name (derived from filename),
 * detected format, and size. Foundation for multi-spec api phase fan-out (swp-530y).
 *
 * Used by the foreman to fan out the api phase with one api-otter invocation per spec.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readdirSync, readFileSync, statSync, existsSync } from 'fs';
import { join, extname, basename } from 'path';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SpecInfo {
  path: string; // relative path from projectRoot, e.g. "./specs/noaa-weather.yaml"
  fileName: string; // e.g. "noaa-weather.yaml"
  integrationName: string; // derived from fileName: "noaa-weather"
  format: 'openapi' | 'asyncapi' | 'unknown';
  size: number; // bytes
}

export interface ListSpecsResult {
  projectRoot: string;
  specs: SpecInfo[];
  totalCount: number;
}

export interface ListSpecsInput {
  projectRoot: string;
}

// ---------------------------------------------------------------------------
// Name derivation
// ---------------------------------------------------------------------------

/**
 * Suffixes stripped from spec filenames to yield the integration name.
 * Only the FIRST matching suffix is stripped (no double-strip).
 * Order matters — more specific first.
 */
const STRIP_SUFFIXES = ['-openapi', '-asyncapi', '-api', '-spec'];

const ALLOWED_EXTS = new Set(['.yaml', '.yml', '.json']);

/**
 * Derive integration name from filename by stripping extension and common suffixes.
 *
 * Examples:
 *   "noaa-weather.yaml"           → "noaa-weather"
 *   "openrouteservice-api.json"   → "openrouteservice"
 *   "fhir-us-core-spec.yaml"      → "fhir-us-core"
 *   "ais-asyncapi.yaml"           → "ais"
 */
export function deriveIntegrationName(fileName: string): string {
  let name = basename(fileName, extname(fileName));
  for (const suffix of STRIP_SUFFIXES) {
    if (name.endsWith(suffix)) {
      name = name.slice(0, -suffix.length);
      break; // only strip one suffix — no double-strip
    }
  }
  return name;
}

// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------

const OPENAPI_MARKERS = ['"openapi":', 'openapi:'];
const ASYNCAPI_MARKERS = ['"asyncapi":', 'asyncapi:'];

/**
 * Detect spec format from a snippet of the file content.
 * Reads at most the first 2KB to keep this fast for large specs.
 */
export function detectFormat(snippet: string): 'openapi' | 'asyncapi' | 'unknown' {
  if (OPENAPI_MARKERS.some((m) => snippet.includes(m))) return 'openapi';
  if (ASYNCAPI_MARKERS.some((m) => snippet.includes(m))) return 'asyncapi';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing — mirrors validate-yaml-fragment pattern)
// ---------------------------------------------------------------------------

export function handleListSpecs(input: ListSpecsInput): ListSpecsResult {
  const { projectRoot } = input;
  const specsDir = join(projectRoot, 'specs');

  const empty: ListSpecsResult = { projectRoot, specs: [], totalCount: 0 };

  if (!existsSync(specsDir)) return empty;

  let entries: string[];
  try {
    entries = readdirSync(specsDir);
  } catch {
    return empty;
  }

  const specs: SpecInfo[] = [];

  for (const entry of entries) {
    const fullPath = join(specsDir, entry);

    // Skip subdirectories and anything that isn't a plain file
    let stat: ReturnType<typeof statSync>;
    try {
      stat = statSync(fullPath);
    } catch {
      continue;
    }
    if (!stat.isFile()) continue;

    // Filter to recognized spec extensions only
    if (!ALLOWED_EXTS.has(extname(entry).toLowerCase())) continue;

    // Read first 2KB to detect format — fast even for massive specs
    let snippet = '';
    try {
      snippet = readFileSync(fullPath, 'utf-8').slice(0, 2048);
    } catch {
      // leave snippet as empty string — detectFormat returns 'unknown'
    }

    specs.push({
      path: `./specs/${entry}`,
      fileName: entry,
      integrationName: deriveIntegrationName(entry),
      format: detectFormat(snippet),
      size: stat.size,
    });
  }

  // Alphabetically sorted by integrationName (consistent foreman fan-out order)
  specs.sort((a, b) => a.integrationName.localeCompare(b.integrationName));

  return { projectRoot, specs, totalCount: specs.length };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerListSpecsTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_list_specs',
    "Enumerate OpenAPI/AsyncAPI specs in the project's specs/ directory. Returns one entry per spec with integration name (derived from filename), detected format, and size. Used by the foreman to fan out the api phase with one api-otter invocation per spec.",
    {
      projectRoot: z.string().describe('Absolute path to the project root directory'),
    },
    async ({ projectRoot }) => {
      const result = handleListSpecs({ projectRoot });
      return {
        content: [{ type: 'text', text: JSON.stringify(result, null, 2) }],
      };
    }
  );
}
